import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import ConfirmModal from '../components/ConfirmModal';
import {
  getPaymentRequest,
  getDocuments,
  getAuditLog,
  approveBhsdRequest,
  approveOnyxRequest,
  rejectRequest,
  editPaymentRequest,
  confirmCreditCardPayment,
  getPayees,
  getAllocations,
  getBhsdUsers,
  uploadDocument,
  deleteDocument,
} from '../services/api';
import FileUpload from '../components/FileUpload';
import { useToken } from '../hooks/useToken';
import { useCurrentUser, isOnyxFC, canSecondApprove } from '../context/UserContext';
import type { PaymentRequest, Document, AuditLog, Payee, Allocation, BhsdUser } from '../types';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}
function formatDate(s: string | null | undefined) {
  if (!s) return '—';
  // SQL Date columns arrive as UTC-midnight ISO strings (e.g. "2026-05-04T00:00:00.000Z").
  // Slicing to YYYY-MM-DD then appending T00:00:00 forces local-time parsing so the
  // timezone offset never shifts the displayed date back by one day.
  return new Date(s.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDateTime(s: string | null | undefined) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function auditLabel(action: string, newValues: Record<string, unknown> | null): string {
  switch (action) {
    case 'create': return 'BHSD Approval';
    case 'approve': return 'Onyx Approval';
    case 'bhsd_second_approval': return 'Second BHSD Approval';
    case 'bhsd_second_rejection': return 'Rejected — BHSD Second Review';
    case 'reject': return 'Rejected';
    case 'payment_confirmed': return 'Payment Complete';
    case 'field_edit': {
      const field = newValues && Object.keys(newValues)[0];
      if (field === 'amount') return 'Amount Edited';
      if (field === 'payee_id') return 'Payee Edited';
      if (field === 'allocation_id') return 'Allocation Edited';
      if (field === 'bhsd_approver_notes') return 'Notes Edited';
      if (field === 'requested_by') return 'BHSD Approver Edited';
      if (field === 'request_date') return 'BHSD Approval Date Edited';
      return 'Field Edited';
    }
    case 'status_change': return 'Status Changed';
    case 'qbo_bill_error': return 'QBO Error';
    default: return action.replace(/_/g, ' ');
  }
}

type Modal = 'approve-bhsd' | 'approve-onyx' | 'reject' | 'confirm-payment' | null;

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const getToken = useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const uploadWarning = (location.state as { uploadWarning?: string } | null)?.uploadWarning ?? null;
  const { user } = useCurrentUser();
  const onyxFC = isOnyxFC(user);
  const secondApprover = canSecondApprove(user);

  const [request, setRequest] = useState<PaymentRequest | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLog[]>([]);
  const [payees, setPayees] = useState<Payee[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [bhsdUsers, setBhsdUsers] = useState<BhsdUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editPayeeId, setEditPayeeId] = useState('');
  const [editAllocationId, setEditAllocationId] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editRequestedBy, setEditRequestedBy] = useState('');
  const [editRequestDate, setEditRequestDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal state
  const [modal, setModal] = useState<Modal>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load() {
    const numId = Number(id);
    if (!numId) { setError('Invalid request ID'); setLoading(false); return; }
    try {
      const token = await getToken();
      const [req, docs, audit] = await Promise.all([
        getPaymentRequest(token, numId),
        getDocuments(token, numId).catch(() => [] as Document[]),
        getAuditLog(token, numId).catch(() => [] as AuditLog[]),
      ]);
      setRequest(req);
      setDocuments(docs);
      setAuditLog(audit);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load reference data when entering edit mode
  async function startEdit() {
    if (!request) return;
    const token = await getToken();
    const fetches: Promise<unknown>[] = [];
    if (payees.length === 0 || allocations.length === 0) {
      fetches.push(
        getPayees(token).then((p) => setPayees(p.data.filter((x) => x.is_active))),
        getAllocations(token).then((a) => setAllocations(a.data)),
      );
    }
    if (bhsdUsers.length === 0) {
      fetches.push(getBhsdUsers(token).then(setBhsdUsers));
    }
    await Promise.all(fetches);
    setEditAmount(String(request.amount));
    setEditPayeeId(String(request.payee_id));
    setEditAllocationId(String(request.allocation_id));
    setEditNotes(request.bhsd_approver_notes ?? '');
    setEditRequestedBy(String(request.requested_by));
    setEditRequestDate(request.request_date.substring(0, 10));
    setEditing(true);
  }

  async function saveEdit() {
    if (!request) return;
    setSaving(true);
    try {
      const token = await getToken();
      const parsedAmount = parseFloat(editAmount.replace(/[^0-9.]/g, ''));
      await editPaymentRequest(token, request.payment_request_id, {
        amount: isNaN(parsedAmount) ? undefined : parsedAmount,
        payee_id: Number(editPayeeId) || undefined,
        allocation_id: Number(editAllocationId) || undefined,
        bhsd_approver_notes: editNotes.trim() || null,
        requested_by: Number(editRequestedBy) || undefined,
        request_date: editRequestDate || undefined,
      });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveBhsd() {
    if (!request) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await getToken();
      await approveBhsdRequest(token, request.payment_request_id);
      setModal(null);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApproveOnyx() {
    if (!request) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await getToken();
      await approveOnyxRequest(token, request.payment_request_id);
      setModal(null);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!request || !rejectionReason.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await getToken();
      await rejectRequest(token, request.payment_request_id, rejectionReason.trim());
      setModal(null);
      setRejectionReason('');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleConfirmPayment() {
    if (!request) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await getToken();
      await confirmCreditCardPayment(token, request.payment_request_id);
      setModal(null);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDocumentUpload(files: File[]) {
    if (!request) return;
    const token = await getToken();
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('payment_request_id', String(request.payment_request_id));
      formData.append('document_type', 'Other');
      await uploadDocument(token, formData);
    }
    setDocuments(await getDocuments(token, request.payment_request_id).catch(() => []));
  }

  async function handleDocumentDelete(documentId: number) {
    const token = await getToken();
    await deleteDocument(token, documentId);
    setDocuments((prev) => prev.filter((d) => d.document_id !== documentId));
  }

  if (loading) {
    return (
      <Layout pageTitle="Request Detail">
        <div className="text-sm text-gray-400">Loading...</div>
      </Layout>
    );
  }
  if (error || !request) {
    return (
      <Layout pageTitle="Request Detail">
        <p className="text-sm text-red-600">{error ?? 'Request not found.'}</p>
      </Layout>
    );
  }

  const isPendingBhsd = request.status_name === 'Pending BHSD Approval';
  const isPendingOnyx = request.status_name === 'Pending Onyx Approval';

  const canEdit = onyxFC && (isPendingBhsd || isPendingOnyx) && !editing;

  // Second BHSD approval: canSecondApprove AND not the submitter (maker-checker)
  const canActBhsd = secondApprover && isPendingBhsd && user?.app_user_id !== request.requested_by;
  // Maker-checker blocked: user could normally approve but submitted this one themselves
  const makerCheckerBlocked = secondApprover && isPendingBhsd && user?.app_user_id === request.requested_by;
  // Onyx approval
  const canActOnyx = onyxFC && isPendingOnyx;

  const canConfirmCreditCard =
    onyxFC &&
    request.status_name === 'Pending Payment' &&
    request.method_name === 'Credit Card';

  return (
    <Layout pageTitle={`Request ${request.request_number}`}>
      {/* Document upload warning from submission page */}
      {uploadWarning && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800 whitespace-pre-wrap">
          <strong>Request submitted successfully.</strong> However, {uploadWarning.replace(/^\d+ document\(s\) failed to upload — /, '')}
        </div>
      )}

      {/* Modals */}
      {modal === 'approve-bhsd' && (
        <ConfirmModal
          title="Approve Request"
          confirmLabel="Approve"
          confirmClass="bg-green-600 hover:bg-green-700 text-white"
          onConfirm={handleApproveBhsd}
          onCancel={() => { setModal(null); setActionError(null); }}
          loading={actionLoading}
        >
          <p>Approve the payment of <strong>{formatCurrency(request.amount)}</strong> to <strong>{request.payee_name}</strong>? This will send the request to Onyx for final review.</p>
          {actionError && <p className="mt-2 text-red-600 text-xs">{actionError}</p>}
        </ConfirmModal>
      )}
      {modal === 'approve-onyx' && (
        <ConfirmModal
          title="Approve Payment"
          confirmLabel="Approve Payment"
          confirmClass="bg-green-600 hover:bg-green-700 text-white"
          onConfirm={handleApproveOnyx}
          onCancel={() => { setModal(null); setActionError(null); }}
          loading={actionLoading}
        >
          <p>Are you sure you want to approve this payment of <strong>{formatCurrency(request.amount)}</strong> to <strong>{request.payee_name}</strong>?</p>
          {actionError && <p className="mt-2 text-red-600 text-xs">{actionError}</p>}
        </ConfirmModal>
      )}
      {modal === 'reject' && (
        <ConfirmModal
          title="Reject Request"
          confirmLabel="Reject Request"
          confirmClass="bg-red-600 hover:bg-red-700 text-white"
          onConfirm={handleReject}
          onCancel={() => { setModal(null); setRejectionReason(''); setActionError(null); }}
          loading={actionLoading}
        >
          <div className="space-y-3">
            <p>Please provide a reason for rejecting this request.</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              placeholder="Rejection reason (required)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            {actionError && <p className="text-red-600 text-xs">{actionError}</p>}
          </div>
        </ConfirmModal>
      )}
      {modal === 'confirm-payment' && (
        <ConfirmModal
          title="Mark as Paid"
          confirmLabel="Confirm Payment"
          confirmClass="bg-blue-600 hover:bg-blue-700 text-white"
          onConfirm={handleConfirmPayment}
          onCancel={() => { setModal(null); setActionError(null); }}
          loading={actionLoading}
        >
          <p>Confirm that the credit card payment of <strong>{formatCurrency(request.amount)}</strong> to <strong>{request.payee_name}</strong> has been completed?</p>
          {actionError && <p className="mt-2 text-red-600 text-xs">{actionError}</p>}
        </ConfirmModal>
      )}

      <div className="flex gap-6">
        {/* Main column */}
        <div className="flex-1 space-y-5">
          {/* Header row */}
          <div className="flex items-start justify-between">
            <div>
              <span className="font-mono text-sm text-gray-500">{request.request_number}</span>
              <div className="mt-1"><StatusBadge status={request.status_name} /></div>
            </div>
            {canEdit && (
              <button
                onClick={startEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Edit
              </button>
            )}
            {editing && (
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Detail card */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
            <DetailRow label="Payee">
              {editing ? (
                <select
                  value={editPayeeId}
                  onChange={(e) => setEditPayeeId(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  {payees.map((p) => (
                    <option key={p.payee_id} value={p.payee_id}>{p.payee_name}</option>
                  ))}
                </select>
              ) : (
                <span>{request.payee_name}</span>
              )}
            </DetailRow>

            <DetailRow label="Amount">
              {editing ? (
                <input
                  type="text"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                />
              ) : (
                <span className="font-medium">{formatCurrency(request.amount)}</span>
              )}
            </DetailRow>

            <DetailRow label="Fund Pool / Project">
              {editing ? (
                <select
                  value={editAllocationId}
                  onChange={(e) => setEditAllocationId(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  {allocations.map((a) => (
                    <option key={a.allocation_id} value={a.allocation_id}>
                      {a.fund_pool_description ?? a.fund_pool_code} / {a.project_name}
                    </option>
                  ))}
                </select>
              ) : (
                <span>{request.fund_pool_description ?? request.fund_pool_code} / {request.project_name}</span>
              )}
            </DetailRow>

            <DetailRow label="Payment Method">
              <span>{request.method_name}</span>
            </DetailRow>

            <DetailRow label="BHSD Approver">
              {editing ? (
                <select
                  value={editRequestedBy}
                  onChange={(e) => setEditRequestedBy(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  {bhsdUsers.map((u) => (
                    <option key={u.app_user_id} value={u.app_user_id}>{u.display_name}</option>
                  ))}
                </select>
              ) : (
                <span>{request.requested_by_name}</span>
              )}
            </DetailRow>

            <DetailRow label="BHSD Approver Notes">
              {editing ? (
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded resize-none"
                />
              ) : (
                <span className="whitespace-pre-wrap">{request.bhsd_approver_notes || '—'}</span>
              )}
            </DetailRow>

            {request.status_name === 'Rejected' && request.rejection_reason && (
              <DetailRow label="Rejection Reason">
                <span className="text-red-700">{request.rejection_reason}</span>
              </DetailRow>
            )}

            {/* Four-date audit chain */}
            <div className="border-t border-gray-100 pt-4 grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">BHSD Approval Date</p>
                {editing ? (
                  <input
                    type="date"
                    value={editRequestDate}
                    max={new Date().toISOString().substring(0, 10)}
                    onChange={(e) => setEditRequestDate(e.target.value)}
                    className="mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded w-full"
                  />
                ) : (
                  <p className="mt-0.5 text-sm text-gray-900">{formatDate(request.request_date)}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">BHSD Second Approval Date</p>
                <p className="mt-0.5 text-sm text-gray-900">
                  {request.bhsd_second_approval_date
                    ? `${formatDateTime(request.bhsd_second_approval_date)}${request.bhsd_second_approver_name ? ` — ${request.bhsd_second_approver_name}` : ''}`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Onyx Approval Date</p>
                <p className="mt-0.5 text-sm text-gray-900">
                  {request.approval_date ? `${formatDate(request.approval_date)} — ${request.approved_by_name ?? ''}` : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Date</p>
                <p className="mt-0.5 text-sm text-gray-900">{formatDate(request.payment_date)}</p>
              </div>
            </div>

            {/* QBO / payment reference fields — Onyx only */}
            {onyxFC && (request.qbo_bill_id || request.qbo_payment_id || request.check_number) && (
              <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">QBO Bill ID</p>
                  <p className="mt-0.5 text-sm text-gray-900 font-mono">{request.qbo_bill_id ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">QBO Payment ID</p>
                  <p className="mt-0.5 text-sm text-gray-900 font-mono">{request.qbo_payment_id ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Check Number</p>
                  <p className="mt-0.5 text-sm text-gray-900 font-mono">{request.check_number ?? '—'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Documents — always shown so Leon can confirm docs before approving */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Supporting Documents</h3>
            {documents.length === 0 ? (
              <p className="text-sm text-gray-400 mb-3">No documents were attached to this request.</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {documents.map((d) => (
                  <li key={d.document_id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{d.file_name}</span>
                    <div className="flex items-center gap-3 ml-4">
                      <a
                        href={d.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View ↗
                      </a>
                      {onyxFC && (
                        <button
                          onClick={() => handleDocumentDelete(d.document_id)}
                          className="text-red-500 hover:text-red-700 font-medium"
                          title="Delete document"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {onyxFC && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500 mb-2">Upload Additional Documents</p>
                <FileUpload onFiles={handleDocumentUpload} />
              </div>
            )}
          </div>

          {/* Second BHSD approval actions */}
          {canActBhsd && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setModal('approve-bhsd')}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Approve
              </button>
              <button
                onClick={() => setModal('reject')}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Reject Request
              </button>
            </div>
          )}

          {/* Maker-checker blocked: user submitted this request themselves */}
          {makerCheckerBlocked && (
            <p className="pt-2 text-sm text-gray-500 italic">
              Awaiting second approval from another reviewer.
            </p>
          )}

          {/* Onyx approval actions */}
          {canActOnyx && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setModal('approve-onyx')}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Approve Payment
              </button>
              <button
                onClick={() => setModal('reject')}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Reject Request
              </button>
            </div>
          )}

          {/* Mark as Paid — Onyx only, Credit Card in Pending Payment */}
          {canConfirmCreditCard && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setModal('confirm-payment')}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Mark as Paid
              </button>
            </div>
          )}
        </div>

        {/* Right panel: Recent History */}
        <div className="w-72 flex-shrink-0">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent History</h3>
            {auditLog.length === 0 ? (
              <p className="text-xs text-gray-400">No history yet.</p>
            ) : (
              <ol className="relative border-l border-gray-200 space-y-4 ml-2">
                {auditLog.map((entry) => (
                  <li key={entry.audit_log_id} className="ml-4">
                    <div className="absolute w-2 h-2 bg-blue-400 rounded-full -left-1 mt-1.5" />
                    <p className="text-xs font-medium text-gray-900">
                      {auditLabel(entry.action, entry.new_values)}
                    </p>
                    {entry.notes && (
                      <p className="text-xs text-gray-500 mt-0.5">{entry.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {entry.performed_by_name ?? 'System'} · {formatDateTime(entry.performed_at)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div className="mt-3">
            <button
              onClick={() => navigate(-1)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="text-sm text-gray-500 w-44 flex-shrink-0">{label}</span>
      <div className="text-sm text-gray-900 flex-1">{children}</div>
    </div>
  );
}
